<template>
  <div class="emoji-editor">
    <tinymce
      :id="editorId"
      v-model="content"
      toolbar1="formatselect | bold italic  strikethrough  forecolor backcolor | link | alignleft aligncenter alignright alignjustify  | numlist bullist outdent indent  | removeformat"
      class="tinymce-editor"
      :other_options="tinymceOptions"
      :lazy="lazy"
    />
  </div>
</template>

<script>
import 'emoji-mart-vue/css/emoji-mart.css';
import tinymce from 'vue-tinymce-editor';
import { generateRandomString } from '@/components/util';
import debounce from 'lodash.debounce';

export default {
  name: 'CustomVueEditor',
  components: {
    tinymce
  },
  props: {
    value: { type: String, default: '' },
    emoji: { type: Boolean, default: true },
    placeholder: { type: String, default: '지표에 대해 보고할 내용을 직접 작성합니다.' },
    height: { type: String, default: '300' },
    lazy: { type: Boolean, default: true }
  },
  data() {
    return {
      original_content: this.value || '',
      content: '',
      editor: null
    };
  },
  computed: {
    editorId() {
      return generateRandomString(5);
    },
    tinymceOptions() {
      return {
        debouncedEmit: null,
        relative_urls: false,
        remove_script_host: false,
        convert_urls: false,
        file_picker_types: 'image',
        file_picker_callback: (callback, value, meta) => {
          if (meta.filetype === 'image') {
            const input = document.createElement('input');
            input.setAttribute('type', 'file');
            input.setAttribute('accept', 'image/jpeg,image/png,image/webp,image/svg+xml');
            input.onchange = async () => {
              const file = input.files[0];
              const base64 = await this.fileToBase64(file);

              const id = 'blobid' + new Date().getTime();
              const blobCache = this.editor.editorUpload.blobCache; // ← 여기서 this.editor 사용
              const blobInfo = blobCache.create(id, file, base64.split(',')[1]);
              blobCache.add(blobInfo);
              const uploadedUrl = await this.upload_image(blobInfo);
              callback(uploadedUrl, { title: file.name });
            };

            input.click();
          }
        },

        plugins: [
          // 'advlist autolink lists link image charmap print preview hr anchor pagebreak',
          // 'searchreplace wordcount visualblocks visualchars code fullscreen',
          // 'insertdatetime media nonbreaking save table contextmenu directionality',
          // 'template paste textcolor colorpicker textpattern imagetools toc help emoticons hr codesample'
          'advlist autolink lists link image print preview hr anchor pagebreak',
          'searchreplace wordcount visualblocks visualchars code',
          'insertdatetime media nonbreaking save table contextmenu directionality',
          'paste textcolor colorpicker textpattern imagetools toc emoticons hr codesample'
        ],
        // menubar: 'file edit view insert format tools table',
        menubar: 'edit view insert format table',
        paste_data_images: true,
        // images_upload_handler: this.upload_image, // 이미지 업로드 핸들러 비활성화 (중요)
        images_upload_handler: null, // 또는 제거
        automatic_uploads: false,
        file_picker_types: '',

        // ✅ 이미지 대화상자 설정
        image_title: true,
        image_dimensions: true,
        image_uploadtab: false, // 핵심 설정!

        height: this.height,
        language: 'ko_KR',
        indent: false,
        remove_linebreaks: true,
        language_url: '/etc/mce_ko_KR.js',
        images_file_types: 'jpeg,jpg,png,gif,svg,webp',
        // images_file_types: 'jpg,svg,webp',
        setup: editor => {
          this.editor = editor;
          editor.on('Undo', () => {
            const content = editor.getContent({ format: 'text' }).trim();
            if (!content) {
              // 내용이 비었을 경우 초기값 복구
              editor.setContent(this.original_content);
            }
          });

          editor.on('NodeChange', e => {
            const imgs = editor.getBody().querySelectorAll('img');

            imgs.forEach(img => {
              const parent = img.parentElement;
              if (!parent || parent.tagName === 'DIV') {
                // 이미 div라면 검사
                const isFlex = parent?.style?.display === 'flex';
                if (!isFlex) {
                  const wrapper = document.createElement('div');
                  wrapper.style.display = 'flex';

                  parent?.replaceChild(wrapper, img);
                  wrapper.appendChild(img);
                }
              } else {
                // div가 아닌 다른 태그일 경우
                const wrapper = document.createElement('div');
                wrapper.style.display = 'flex';

                parent.replaceChild(wrapper, img);
                wrapper.appendChild(img);
              }
            });
          });

          editor.on('blur', event => {
            this.handleBlur(event);
          });

          editor.addShortcut('ctrl+s', 'Custom save', () => {
            this.$emit('save-content');
            setTimeout(this.hideTinyMCEError, 1);
          });
        }
      };
    }
  },
  watch: {
    async content(newVal) {
      if (this.lazy) {
        this.debouncedEmit(newVal);
      } else {
        this.$emit('input', await this.replaceBase64WithUrl(newVal));
      }
    },
    value(newVal) {
      // if (newVal !== this.content) {
      //   this.content = newVal;
      // }
    }
  },

  mounted() {
    this.$nextTick(() => {
      if (!this.value) {
        this.content = '';
      } else {
        this.content = this.value;
      }
    });
  },

  created() {
    this.debouncedEmit = debounce(async val => {
      this.$emit('input', await this.replaceBase64WithUrl(val));
    }, 1000);
  },
  methods: {
    async handleBlur(event) {
      const replaced = await this.replaceBase64WithUrl(this.content);
      this.$emit('input', replaced);
      this.$emit('blur', event);
    },
    hideTinyMCEError() {
      const errorAlert = document.querySelector('.mce-notification-error');
      if (errorAlert) {
        errorAlert.style.display = 'none';
      }
    },
    async upload_image(blobInfo) {
      try {
        let base64;

        if (typeof blobInfo === 'string' && blobInfo.startsWith('data:image/')) {
          // 이미 base64 문자열인 경우 (replaceBase64WithUrl에서 호출)
          base64 = blobInfo;
        } else if (blobInfo?.blob) {
          // BlobInfo 객체인 경우 (images_upload_handler)
          const file = blobInfo.blob();
          base64 = await this.fileToBase64(file);
        } else {
          throw new Error('Invalid image input for upload_image');
        }

        const response = await axios.post(this.$store.state.backend_host + '/upload_file_to_s3', {
          image: base64,
          report_code: this.$store.state.report_list.report_code
        });

        if (response?.data?.url) {
          return response.data.url;
        } else {
          return null;
        }
      } catch (error) {
        this.$Sentry.captureException(error);
        return null;
      }
    },

    async replaceBase64WithUrl(text) {
      /**
       * 문자열 내의 Base64 인코딩된 이미지를 찾아 upload_image()를 호출하여 업로드 후,
       * 해당 Base64 부분을 URL로 교체하는 함수.
       */

      if (typeof text !== 'string') {
        return text; // 문자열이 아니면 바로 리턴
      }
      const base64Pattern = /data:image\/[a-zA-Z]+;base64,([A-Za-z0-9+/=]+)/g;

      // 비동기 처리를 위해 모든 Base64 데이터를 찾고, 순차적으로 업로드 후 치환
      const matches = [...text.matchAll(base64Pattern)];

      for (const match of matches) {
        const base64Data = match[0];
        const uploadedUrl = await this.upload_image(base64Data);

        if (uploadedUrl) {
          text = text.replace(base64Data, uploadedUrl);
        }
      }

      return text;
    },
    async handleImageAdded(file, Editor, cursorLocation, resetUploader) {
      try {
        // upload_image메소드가 base64기준으로 받고 있어서 일부러 한번 변환함
        const base64String = await this.fileToBase64(file);
        const url = await this.upload_image(base64String);
        Editor.insertEmbed(cursorLocation, 'image', url);
        resetUploader();
      } catch (error) {
        this.$Sentry.captureException(error);
      }
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
      });
    }
  }
};
</script>

<style scoped>
.emoji-editor {
  position: relative;
}

.rounded-editor {
  border-radius: 0.2rem;
  background-color: white;
}

.emoji-wrapper {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 1;
}

.emoji-button {
  border-radius: 0.25rem;
  padding: 5px 5px;
  font-size: 1.2em;
}

.ql-flip {
  z-index: 10 !important;
  left: 0 !important;
}
</style>
